<script setup lang="ts">
/**
 * Onboarding tour — step 5 body (M4C-18), the final step.
 *
 * "Light your first star.": a full "sky" of faint dashed constellations with one
 * active gold node labelled "Start here", above the heading and a Body-Strong
 * call to action. The sky is a single hand-authored, hex-free inline SVG (colored
 * through the `star` and `fontColor` tokens) so it carries no expiring Figma asset
 * URLs. Desktop spreads the constellations across a wide 840×170 strip; the
 * narrower mobile frame repacks the same constellations into a 304×186 cluster, so
 * the two arrangements are authored as separate viewBoxes toggled at the `md`
 * breakpoint (the shell uses the same breakpoint for its desktop/mobile chrome).
 *
 * Each dot is rendered as its own `<circle>` at a computed centre rather than by
 * scaling a group, so a constellation's normalised shape can be mapped into any
 * bounding box (desktop strip or exact mobile box) without distorting the dots.
 * The chrome (progress header, footer nav, border, backdrop) is owned by the tour
 * shell (M4C-13); this component renders body content only.
 */
const { t } = useI18n();

interface Dot {
  x: number;
  y: number;
  gold?: boolean;
}

interface Constellation {
  /** Dot centres. */
  dots: Dot[];
  /** Dashed links between dots, as pairs of `dots` indices. */
  links: [number, number][];
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Normalised constellation shapes (dot coordinates in a 0–1 box). The gold dot in
 * `gold` is the active "Start here" node; its geometry is traced from the Figma
 * node, the faint surrounding constellations from the rendered frame.
 */
const SHAPE_FLAT: Constellation = {
  dots: [
    { x: 0.02, y: 0.08 },
    { x: 0.66, y: 0.52 },
    { x: 0.86, y: 0.74 },
    { x: 0.97, y: 1 },
  ],
  links: [
    [0, 1],
    [1, 2],
    [2, 3],
  ],
};

const SHAPE_GOLD: Constellation = {
  dots: [
    { x: 0.025, y: 0.692 },
    { x: 0.203, y: 1, gold: true },
    { x: 0.444, y: 0.275 },
    { x: 0.65, y: 0.462 },
    { x: 0.769, y: 0.044 },
    { x: 1, y: 0.593 },
  ],
  links: [
    [1, 0],
    [0, 2],
    [2, 4],
    [2, 3],
    [3, 5],
  ],
};

const SHAPE_MID: Constellation = {
  dots: [
    { x: 0.3, y: 0 },
    { x: 0.92, y: 0.16 },
    { x: 0.04, y: 0.42 },
    { x: 0.55, y: 0.52 },
    { x: 1, y: 0.46 },
    { x: 0.4, y: 1 },
    { x: 0.62, y: 0.8 },
  ],
  links: [
    [0, 2],
    [0, 3],
    [1, 3],
    [3, 4],
    [2, 5],
    [5, 6],
    [3, 6],
  ],
};

const SHAPE_Y: Constellation = {
  dots: [
    { x: 0.08, y: 0 },
    { x: 0.34, y: 0.45 },
    { x: 0.28, y: 1 },
    { x: 1, y: 0.6 },
  ],
  links: [
    [0, 1],
    [1, 2],
    [1, 3],
  ],
};

/** Map a normalised shape into an absolute bounding box in the SVG's viewBox space. */
function place(shape: Constellation, box: Box): Constellation {
  return {
    dots: shape.dots.map((dot) => ({
      x: box.x + dot.x * box.w,
      y: box.y + dot.y * box.h,
      gold: dot.gold,
    })),
    links: shape.links,
  };
}

/** The gold dot anchors the "Start here" label. */
function goldDot(constellations: Constellation[]): Dot {
  for (const constellation of constellations) {
    const gold = constellation.dots.find((dot) => dot.gold);
    if (gold) {
      return gold;
    }
  }
  return { x: 0, y: 0 };
}

interface SkyLayout {
  key: string;
  viewBox: string;
  /** Responsive visibility (desktop strip vs. mobile cluster). */
  visibilityClass: string;
  constellations: Constellation[];
  label: Dot;
}

/** Desktop sky — constellations spread across the 840×170 strip. */
const desktopConstellations: Constellation[] = [
  place(SHAPE_FLAT, { x: 6, y: 120, w: 134, h: 50 }),
  place(SHAPE_GOLD, { x: 176.5, y: 0, w: 160, h: 91 }),
  place(SHAPE_MID, { x: 398, y: 33, w: 86, h: 104 }),
  place(SHAPE_Y, { x: 705, y: 15, w: 100, h: 95 }),
];

/** Mobile sky — same constellations repacked into the 304×186 cluster (exact Figma boxes). */
const mobileConstellations: Constellation[] = [
  place(SHAPE_FLAT, { x: 0, y: 109.7, w: 87.6, h: 34.9 }),
  place(SHAPE_GOLD, { x: 72.6, y: 48, w: 104.7, h: 73 }),
  place(SHAPE_MID, { x: 176.6, y: 78.7, w: 58.7, h: 67.3 }),
  place(SHAPE_Y, { x: 239.8, y: 40, w: 64.2, h: 63 }),
];

const skyLayouts: SkyLayout[] = [
  {
    key: 'desktop',
    viewBox: '0 0 840 170',
    visibilityClass: 'hidden md:block',
    constellations: desktopConstellations,
    label: goldDot(desktopConstellations),
  },
  {
    key: 'mobile',
    viewBox: '0 0 304 186',
    visibilityClass: 'block md:hidden',
    constellations: mobileConstellations,
    label: goldDot(mobileConstellations),
  },
];
</script>

<template>
  <div class="flex flex-col gap-16">
    <!-- Full-sky illustration: faint constellations + the active gold "Start here" node -->
    <div class="w-full">
      <svg
        v-for="layout in skyLayouts"
        :key="layout.key"
        :viewBox="layout.viewBox"
        fill="none"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        :class="['h-auto w-full', layout.visibilityClass]"
      >
        <!-- Dashed constellation lines -->
        <g
          class="stroke-fontColor-w80"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-dasharray="4 4"
        >
          <template v-for="(constellation, ci) in layout.constellations" :key="`line-${ci}`">
            <line
              v-for="(link, li) in constellation.links"
              :key="`line-${ci}-${li}`"
              :x1="constellation.dots[link[0]].x"
              :y1="constellation.dots[link[0]].y"
              :x2="constellation.dots[link[1]].x"
              :y2="constellation.dots[link[1]].y"
            />
          </template>
        </g>

        <!-- Dots: hollow grey stars, one filled gold "Start here" node -->
        <template v-for="(constellation, ci) in layout.constellations" :key="`dot-${ci}`">
          <circle
            v-for="(dot, di) in constellation.dots"
            :key="`dot-${ci}-${di}`"
            :cx="dot.x"
            :cy="dot.y"
            r="4"
            :class="dot.gold ? 'fill-star' : 'fill-white stroke-fontColor-w80'"
            stroke-width="1.5"
          />
        </template>

        <!-- "Start here" label tied to the gold node -->
        <text
          :x="layout.label.x - 4"
          :y="layout.label.y + 23"
          class="fill-fontColor font-bold"
          font-size="14"
        >
          {{ t('onboarding.tour.step5.start_here') }}
        </text>
      </svg>
    </div>

    <!-- Heading + body (40px gap) -->
    <div class="flex flex-col gap-10">
      <p class="text-h1">{{ t('onboarding.tour.step5.heading') }}</p>
      <p class="text-body-strong">{{ t('onboarding.tour.step5.body') }}</p>
    </div>
  </div>
</template>
